import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useChatbot } from "./useChatbot";
import ChatMessage from "./ChatMessage";
import ChatInput from "./ChatInput";
import PreviewSidebar from "./PreviewSidebar";
import "./Chatbot.css";

const QUICK_STARTER_PROMPTS = [
  {
    icon: "⚡",
    label: "Departures in Next 30 Days",
    desc: "Confirmed group departures & filling-fast seats",
    query: "What packages and confirmed group departures do you have in the next 30 days?"
  },
  {
    icon: "🏔️",
    label: "Sikkim & Darjeeling Guide",
    desc: "Scenic 5-day itineraries with top sights & monasteries",
    query: "Plan a scenic 5-day tour to Gangtok & Darjeeling with top sights and places to visit"
  },
  {
    icon: "🚗",
    label: "Mountain Cabs from Bagdogra",
    desc: "Verified cabs, SUVs & shared mountain rides",
    query: "Check cab availability and rates from Bagdogra to Gangtok with vehicle options"
  },
  {
    icon: "🏨",
    label: "Boutique Mountain Stays",
    desc: "Handpicked valley & riverside cottages in Sikkim and Manali",
    query: "Recommend handpicked luxury boutique stays in Sikkim"
  }
];

const SUGGESTION_CHIPS = [
  "⚡ Next 30 Days Packages",
  "🏔️ Gangtok & Darjeeling Itinerary",
  "🚗 Bagdogra to Gangtok Cabs",
  "🏨 Boutique Stays in Sikkim",
  "⭐ Top Monasteries in Sikkim"
];

/**
 * Full-page TGH Luxury AI Concierge with Generative UI & Split-Screen Sidebar
 */
export default function ChatbotPage() {
  const { messages, isLoading, preview, setPreview, closePreview, sendMessage, clearChat } = useChatbot();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  // Scope the "hide legacy floating widget" CSS to this page only
  useEffect(() => {
    document.body.classList.add("tgh-chatbot-active");
    return () => document.body.classList.remove("tgh-chatbot-active");
  }, []);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const wrapperClass = preview ? "tgh-chatbot-wrapper tgh-chatbot-wrapper--split" : "tgh-chatbot-wrapper";

  // "Open Page": close the sidebar and go to the page in the SAME tab
  const goToPage = (url) => {
    closePreview();
    try {
      const u = new URL(url, window.location.origin);
      if (u.origin === window.location.origin) {
        navigate(u.pathname + u.search + u.hash);
        window.scrollTo(0, 0);
      } else {
        window.location.assign(u.href);
      }
    } catch (e) {
      window.location.assign(url);
    }
  };

  // Show hero state if there is only the welcome message
  const showHero = messages.length <= 1;

  return (
    <div className={wrapperClass}>
      {/* ── Left / Main Concierge Chat Panel ── */}
      <div className="tgh-chatbot-page">
        {/* Luxury Top Header */}
        <div className="tgh-chatbot-header">
          <div className="tgh-chatbot-header-left">
            <Link to="/" className="tgh-chatbot-back-btn" title="Return to The Great Himalayas">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Back to TGH
            </Link>

            <div className="tgh-chatbot-avatar-lg">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
            </div>

            <div className="tgh-chatbot-title-box">
              <h1 className="tgh-chatbot-title">
                Aria <span className="tgh-chatbot-title-pill">AI Concierge</span>
              </h1>
              <p className="tgh-chatbot-subtitle">
                <span className="tgh-chatbot-status-dot" /> Online • Official Himalayan Travel Specialist
              </p>
            </div>
          </div>

          <div className="tgh-chatbot-header-actions">
            {preview && (
              <button
                className="tgh-chatbot-toggle-preview-btn"
                onClick={closePreview}
                title="Hide Webpage Split Screen"
              >
                Hide Sidebar
              </button>
            )}
            <button className="tgh-chatbot-clear-btn" onClick={clearChat} title="Start fresh conversation">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="1 4 1 10 7 10" /><polyline points="23 20 23 14 17 14" />
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
              </svg>
              New Chat
            </button>
          </div>
        </div>

        {/* Messages Stream */}
        <div className="tgh-chatbot-messages">
          {/* Hero Welcome Showcase when no user messages yet */}
          {showHero && (
            <div className="tgh-chatbot-hero">
              <span className="tgh-hero-badge">Next-Gen Himalayan Travel Specialist</span>
              <h2 className="tgh-hero-title">Where does your journey begin?</h2>
              <p className="tgh-hero-subtitle">
                Explore real-time tour departures, 700+ sightseeing secrets, verified mountain cabs, and handpicked boutique stays across Himachal, Sikkim, Spiti & Kashmir.
              </p>

              <div className="tgh-hero-stats-row">
                <div className="tgh-hero-stat-pill">
                  <span>🏔️</span> <strong>45+</strong> Live Departures
                </div>
                <div className="tgh-hero-stat-pill">
                  <span>📍</span> <strong>700+</strong> Sights & Monasteries
                </div>
                <div className="tgh-hero-stat-pill">
                  <span>🚗</span> <strong>100%</strong> Verified Cabs
                </div>
                <div className="tgh-hero-stat-pill">
                  <span>🏨</span> <strong>Curated</strong> Boutique Stays
                </div>
              </div>

              {/* Starter Quick-Prompt Cards */}
              <div className="tgh-hero-cards-grid">
                {QUICK_STARTER_PROMPTS.map((item, idx) => (
                  <div
                    key={idx}
                    className="tgh-hero-action-card"
                    onClick={() => sendMessage(item.query)}
                    role="button"
                    tabIndex={0}
                  >
                    <span className="tgh-hero-card-icon">{item.icon}</span>
                    <div className="tgh-hero-card-content">
                      <span className="tgh-hero-card-label">{item.label}</span>
                      <span className="tgh-hero-card-desc">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Render Active Message History */}
          {messages.map((msg) => (
            <ChatMessage
              key={msg.id}
              message={msg}
              onOpenPreview={setPreview}
              onAskAbout={sendMessage}
            />
          ))}

          {/* Typing Indicator */}
          {isLoading && (
            <div className="tgh-chat-msg tgh-chat-msg--bot">
              <div className="tgh-chat-avatar">
                <span>A</span>
              </div>
              <div className="tgh-chat-bubble tgh-chat-typing">
                <span /><span /><span />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Dock Area */}
        <div className="tgh-chatbot-input-area">
          {/* Quick Suggestion Chips */}
          <div className="tgh-chatbot-quick-chips">
            {SUGGESTION_CHIPS.map((chip, idx) => (
              <button
                key={idx}
                type="button"
                className="tgh-quick-chip"
                onClick={() => sendMessage(chip.replace(/^[^\w\s]+\s*/, ""))}
              >
                {chip}
              </button>
            ))}
          </div>

          <ChatInput onSend={sendMessage} disabled={isLoading} />
          <p className="tgh-chatbot-disclaimer">
            Real-time Himalayan itineraries & rates. Click any tour, cab or stay to preview details.
          </p>
        </div>
      </div>

      {/* ── Right / Split-Screen sidebar: visual showcase (has id) or live-page iframe ── */}
      {preview && (
        <PreviewSidebar
          key={`${preview.type}-${preview.id}-${preview.url}`}
          preview={preview}
          onClose={closePreview}
          onOpenPage={goToPage}
        />
      )}
    </div>
  );
}